import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import FormComp from "../../Componentes/FormComp";
import formStyle from "../../Componentes/FormComp/FormComp.module.css";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import { formatCPF } from "../../Funcoes/FormatCPF";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

const MODAL_ID = "gestaoUsuariosAdicionar";
const LOOKUP_URL = "/Painel/API/AccessManagement/GestaoUsuarios/LookupByCpf/index.php";
const CREATE_URL = "/Painel/API/AccessManagement/GestaoUsuarios/Create/";

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const FormSection = styled.div`
    width: 100%;
    text-align: left;
    position: relative;

    .${formStyle.formChild} p {
        margin: 15px 0 5px 0;
        padding-left: 2px;
        text-align: left;
    }
`;

const StatusMsg = styled.p`
    margin: 8px 0 0 0;
    font-size: 13px;
    color: ${(p) => (p.$error ? "#b91c1c" : "#64748b")};
`;

const BotoesWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const Botao = styled.button`
    padding: 9px 14px;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#2563eb")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: 0.2s all ease;

    &:disabled {
        background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#93c5fd")};
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#1d4ed8")};
    }
`;

const ModalAdicionarUsuario = ({ onSaved }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();
    const requestIdRef = useRef(0);

    const [cpf, setCpf] = useState("");
    const [nome, setNome] = useState("");
    const [userId, setUserId] = useState("");
    const [email, setEmail] = useState("");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState("");
    const [saving, setSaving] = useState(false);

    const clearColab = useCallback(() => {
        setNome("");
        setLookupError("");
        setLookupLoading(false);
    }, []);

    useEffect(() => {
        const digits = cpf.replace(/\D/g, "");

        if (digits.length !== 11) {
            clearColab();
            return undefined;
        }

        const currentRequest = ++requestIdRef.current;
        setLookupLoading(true);
        setLookupError("");
        setNome("");

        const fetchColab = async () => {
            try {
                const { data } = await api.get(LOOKUP_URL, {
                    params: { cpf: digits },
                });

                if (currentRequest !== requestIdRef.current) return;

                setNome(data?.nome ? String(data.nome).trim() : "");
                setLookupError("");
            } catch (err) {
                if (currentRequest !== requestIdRef.current) return;

                setNome("");
                const msg =
                    err?.response?.data?.message || "Erro ao buscar colaborador pelo CPF.";
                setLookupError(msg);
                notifyError(msg);
            } finally {
                if (currentRequest === requestIdRef.current) {
                    setLookupLoading(false);
                }
            }
        };

        fetchColab();

        return () => {
            requestIdRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpf, clearColab]);

    const handleCpfChange = (e) => {
        const digits = String(e.target.value ?? "")
            .replace(/\D/g, "")
            .slice(0, 11);
        setCpf(digits);
    };

    const handleUserIdChange = (e) => {
        const value = String(e.target.value ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9.]/g, "")
            .replace(/\.{2,}/g, ".")
            .slice(0, 60);
        setUserId(value);
    };

    const handleEmailChange = (e) => {
        setEmail(String(e.target.value ?? "").trimStart());
    };

    const handleClose = () => {
        if (saving) return;
        closeModal(MODAL_ID);
    };

    const canSave = useMemo(() => {
        const digits = cpf.replace(/\D/g, "");
        const login = userId.trim();
        return (
            digits.length === 11 &&
            nome.trim() !== "" &&
            !lookupLoading &&
            !lookupError &&
            /^[a-z0-9]+(\.[a-z0-9]+)+$/.test(login) &&
            !saving
        );
    }, [cpf, nome, userId, lookupLoading, lookupError, saving]);

    const handleSave = useCallback(async () => {
        if (!canSave) return;

        setSaving(true);

        try {
            const { data } = await api.post(CREATE_URL, {
                cpf: cpf.replace(/\D/g, ""),
                nome: nome.trim(),
                user_id: userId.trim(),
                email: email.trim(),
            });

            notifySuccess(data?.message || "Usuário criado com sucesso.");
            await onSaved?.();
            closeModal(MODAL_ID);
        } catch (err) {
            const msg = err?.response?.data?.message || "Erro ao criar usuário.";
            notifyError(msg);
        } finally {
            setSaving(false);
        }
    }, [api, canSave, closeModal, cpf, email, nome, notifyError, notifySuccess, onSaved, userId]);

    return (
        <Overlay state changeState={handleClose}>
            <Modal header close={handleClose} size="md">
                <ModalTitle>ADICIONAR USUÁRIO</ModalTitle>

                <FormSection>
                    <Formulario
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        <FormComp
                            title="CPF"
                            type="text"
                            placeholder="000.000.000-00"
                            valor={formatCPF(cpf)}
                            changeFunc={handleCpfChange}
                            required
                        />
                        {lookupLoading && <StatusMsg>Buscando colaborador...</StatusMsg>}
                        {!lookupLoading && lookupError && (
                            <StatusMsg $error>{lookupError}</StatusMsg>
                        )}
                        <FormComp
                            title="NOME COMPLETO"
                            type="text"
                            valor={nome}
                            readOnly
                            placeholder={
                                cpf.replace(/\D/g, "").length === 11
                                    ? ""
                                    : "Preenchido automaticamente pelo CPF"
                            }
                        />
                        <FormComp
                            title="USUÁRIO"
                            type="text"
                            placeholder="nome.sobrenome"
                            valor={userId}
                            changeFunc={handleUserIdChange}
                            required
                        />
                        <FormComp
                            title="E-MAIL"
                            type="text"
                            placeholder="opcional"
                            valor={email}
                            changeFunc={handleEmailChange}
                        />

                        <BotoesWrapper>
                            <Botao $cancel type="button" onClick={handleClose} disabled={saving}>
                                CANCELAR
                            </Botao>
                            <Botao type="submit" disabled={!canSave}>
                                {saving ? "SALVANDO…" : "SALVAR"}
                            </Botao>
                        </BotoesWrapper>
                    </Formulario>
                </FormSection>
            </Modal>
        </Overlay>
    );
};

export default ModalAdicionarUsuario;
export { MODAL_ID };
